import { ReactNode } from 'react';
import BellIcon from '../../assets/icons/event-bell.svg?react';
import ClockIcon from '../../assets/icons/event-clock.svg?react';
import CooldownIcon from '../../assets/icons/event-cooldown.svg?react';
import HealthIcon from '../../assets/icons/event-health.svg?react';
import MachineIcon from '../../assets/icons/event-machine.svg?react';
import ResolvedIcon from '../../assets/icons/event-resolved.svg?react';
import TriggeredIcon from '../../assets/icons/event-triggered.svg?react';
import {
  SortOrder,
  useGetAlarmsQuery,
  useGetNotificationsQuery,
} from '../../gql/generated';
import {
  MachineEventEntry,
  MachineEventKind,
  toEventLog,
} from './machineEventEpisodes';
import { cn } from '../../utils/className';

const EVENT_LIMIT = 50;
const ALARM_LIMIT = 100;

const KIND_ICONS: Record<MachineEventKind, ReactNode> = {
  health: (
    <HealthIcon
      role='img'
      aria-label='Machine health alert'
      className='h-6 w-6'
    />
  ),
  alarm: (
    <MachineIcon role='img' aria-label='Machine alarm' className='h-6 w-6' />
  ),
};

const pad = (value: number) => String(value).padStart(2, '0');

/** 08.10.2026, as the design writes a date. */
function formatDate(date: Date): string {
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/** 10:27, on a 24-hour clock. */
function formatTime(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Thursday 08.10.2026 */
function formatDay(date: Date): string {
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
  return `${weekday} ${formatDate(date)}`;
}

/** How long an alert has been active: 2h 13m, 45m or 3d 4h. */
function formatDuration(from: Date, to: Date): string {
  const minutes = Math.max(
    0,
    Math.floor((to.getTime() - from.getTime()) / 60000)
  );
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

const STATE_PILLS: Record<MachineEventEntry['state'], ReactNode> = {
  triggered: (
    <StatePill className='text-primary bg-red-050'>
      <TriggeredIcon aria-hidden className='h-3.5 w-3.5' />
      Triggered
    </StatePill>
  ),
  resolved: (
    <StatePill className='text-primary bg-green-050'>
      <ResolvedIcon aria-hidden className='h-3.5 w-3.5' />
      Resolved
    </StatePill>
  ),
  cooldown: (
    <StatePill className='text-primary bg-blue-050 pr-2'>
      <CooldownIcon aria-hidden className='h-3.5 w-3.5' />
      Cooldown
    </StatePill>
  ),
};

/** History entries per day, newest day first. */
function groupByDay(entries: MachineEventEntry[]) {
  const days = new Map<string, MachineEventEntry[]>();
  for (const entry of entries) {
    const day = formatDay(entry.time);
    days.set(day, [...(days.get(day) ?? []), entry]);
  }
  return [...days.entries()];
}

/**
 * The machine's Pulse alerts for the phone and tablet detail page. Active
 * alerts lead, with how long they have run. Every trigger and resolve follows
 * per day, as its own line. An icon tells a machine health alert from a
 * machine alarm.
 */
export function MachineEventsTab({ machineId }: { machineId: string }) {
  const { data, loading, error } = useGetNotificationsQuery({
    variables: {
      filter: { machineIds: [machineId], allUsers: true },
      orderBy: { timeUtc: SortOrder.desc },
      pagination: { limit: EVENT_LIMIT, offset: 0 },
    },
    fetchPolicy: 'cache-and-network',
  });

  // Each alert's own status says reliably whether it is active right now.
  const alarms = useGetAlarmsQuery({
    variables: {
      filter: { machineIds: [machineId], allUsers: true },
      pagination: { limit: ALARM_LIMIT, offset: 0 },
    },
    fetchPolicy: 'cache-and-network',
  });

  if ((loading && !data) || (alarms.loading && !alarms.data)) {
    return <Message>Loading Pulse alerts...</Message>;
  }
  if ((error && !data) || (alarms.error && !alarms.data)) {
    return <Message className='text-error'>Error loading Pulse alerts</Message>;
  }

  const now = new Date();
  const { active, history } = toEventLog(
    data?.notifications.rows ?? [],
    alarms.data?.alarms.rows ?? [],
    now
  );
  if (active.length === 0 && history.length === 0) {
    return <Message>No Pulse alerts recorded</Message>;
  }

  return (
    <div className='bg-surface-focus-support flex flex-col pb-10'>
      {active.length > 0 && (
        <section aria-labelledby='ongoing-alarms'>
          <h3
            id='ongoing-alarms'
            className='size-caption border-border-secondary bg-red-050 flex
              items-center gap-2.5 border-b px-4 py-3 text-red-700 uppercase'
          >
            <BellIcon aria-hidden className='h-4 w-4 shrink-0' />
            Ongoing alarms ({active.length})
          </h3>
          <EpisodeList>
            {active.map((episode) => (
              <EpisodeRow
                key={episode.id}
                title={episode.title}
                kind={episode.kind}
                when={`${formatDate(episode.start)} - Triggered ${formatTime(episode.start)}`}
                status={
                  <StatePill className='bg-red-050 text-red-700'>
                    <ClockIcon aria-hidden className='h-3.5 w-3.5' />
                    {formatDuration(episode.start, now)}
                  </StatePill>
                }
              />
            ))}
          </EpisodeList>
        </section>
      )}

      {groupByDay(history).map(([day, entries]) => (
        <section key={day}>
          <h3
            className='size-caption text-primary border-border-secondary
              border-y px-4 py-3 tracking-[0.08em] uppercase'
          >
            {day}
          </h3>
          <EpisodeList>
            {entries.map((entry) => (
              <EpisodeRow
                key={entry.id}
                title={entry.title}
                kind={entry.kind}
                when={formatTime(entry.time)}
                status={STATE_PILLS[entry.state]}
              />
            ))}
          </EpisodeList>
        </section>
      ))}
    </div>
  );
}

function Message({
  children,
  className = 'text-tertiary',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className='flex flex-col items-center justify-center py-12'>
      <p className={`size-p2 ${className}`}>{children}</p>
    </div>
  );
}

function EpisodeList({ children }: { children: ReactNode }) {
  return (
    <ul
      className='bg-surface-primary divide-border-secondary flex flex-col
        divide-y px-4'
    >
      {children}
    </ul>
  );
}

function StatePill({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        // 14px icons with 6px above and below.
        `size-caption flex shrink-0 items-center gap-1.5 rounded-full py-1.5
        pr-3 pl-2`,
        className
      )}
    >
      {children}
    </span>
  );
}

function EpisodeRow({
  title,
  kind,
  when,
  status,
}: {
  title: string;
  kind: MachineEventKind;
  when: string;
  status?: ReactNode;
}) {
  return (
    <li className='flex items-center gap-5 py-4'>
      <span className='shrink-0'>{KIND_ICONS[kind]}</span>
      <div className='flex min-w-0 flex-1 items-start gap-3'>
        <div className='flex min-w-0 flex-1 flex-col'>
          {/* A title can fall back to an alert name a user typed. Its first
              letter shows as a capital either way. */}
          <span
            className='size-p2 text-primary truncate font-medium
              first-letter:uppercase'
          >
            {title}
          </span>
          <span className='size-caption text-secondary truncate'>{when}</span>
        </div>
        {status}
      </div>
    </li>
  );
}
